import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import useResearchParams from '@/hooks/useResearchParams';
import { useAuth } from '@/components/auth/AuthProvider';

/**
 * Storage keys for the captured research params.
 *
 * Campaign code + participant id are non-PII opaque tokens and live in
 * `localStorage` (listed in `PERSISTENT_KEYS`) so a participant who closes the
 * tab and reopens the bare site keeps their study context. The address is PII,
 * so it lives in `sessionStorage` instead — erased on tab/browser close (kept
 * across an in-session refresh) to match the "erased when you close it"
 * commitment, consistent with the guest-entered address. Explicit sign-out
 * clears all three. Replace-on-new-link (below) keeps a new participant from
 * inheriting a prior one's stored values.
 */
const STORAGE_KEY_CAMPAIGN_CODE = 'votemate_research_campaign_code';
const STORAGE_KEY_PARTICIPANT_ID = 'votemate_research_participant_id';
const STORAGE_KEY_ADDRESS = 'votemate_research_address';

interface ResearchContextValue {
  /** The research campaign identifier currently in effect, or null. */
  campaignCode: string | null;
  /** The opaque participant token currently in effect, or null. */
  participantId: string | null;
  /**
   * Client-only address pre-fill hint from the ?address= link. Cached in
   * `sessionStorage` (not the URL) because the /chat route rewrites its query
   * string on mount, which would otherwise drop the param before the guest
   * landing's address field can consume it; sessionStorage means it is erased
   * when the tab/browser closes. Never sent to the backend.
   */
  address: string | null;
  /**
   * True when at least one of the two research values is set. Convenience flag
   * for call sites that want to skip work when this isn't a research session.
   */
  hasResearchContext: boolean;
}

const ResearchContext = createContext<ResearchContextValue | undefined>(undefined);

const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // localStorage can throw in private modes or when disabled. Treat as
    // "not available" rather than crashing the provider.
    return null;
  }
};

const writeStored = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Same rationale as readStored — silently skip persistence rather than
    // break the app when storage is unavailable.
  }
};

// Session-scoped variants for the address (PII): sessionStorage is erased when
// the tab/browser closes, so the pre-fill hint never outlives the session.
const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, value);
    }
  } catch {
    // Same rationale as readStored.
  }
};

interface ResearchProviderProps {
  children: ReactNode;
}

/**
 * Manages the lifecycle of research collaboration parameters
 * (`campaignCode`, `participantId`) so downstream chat and signup code can
 * read a single source of truth. Designed to support any external
 * research-partner integration.
 *
 * Resolution priority (highest first):
 *   1. URL query params on the current navigation — a fresh research link
 *      always wins, so a participant who refreshes with a new link doesn't
 *      get stuck on stale state.
 *   2. Stored values — campaign/participant in localStorage (survive a tab
 *      close), address in sessionStorage (survives an in-tab refresh, erased
 *      on tab close) — so intra-app navigation that drops the query string
 *      doesn't lose the context.
 *   3. The authenticated user's record — for returning participants who
 *      land logged in without a fresh URL, the values they signed up with
 *      remain associated with their session.
 *
 * Replace-on-new-link: when a URL arrives with a participantId different from
 * the one already stored, the whole stored set is replaced with this link's
 * values. That stops a new participant from inheriting a previous
 * participant's address/campaign left in storage on a shared device.
 *
 * Lives inside AuthProvider (wrapping routes and auth modals) and above
 * ChatProvider so it can read `useAuth().user` and feed values into chat
 * and signup requests.
 */
export const ResearchProvider: React.FC<ResearchProviderProps> = ({ children }) => {
  const {
    campaignCode: urlCampaignCode,
    participantId: urlParticipantId,
    address: urlAddress,
  } = useResearchParams();
  const { user } = useAuth();

  // Seed from the backing store on mount so values survive navigation that
  // drops the query string. Campaign/participant come from localStorage (also
  // survive a tab close); the address comes from sessionStorage (survives an
  // in-tab refresh, erased when the tab closes).
  const [storedCampaignCode, setStoredCampaignCode] = useState<string | null>(() =>
    readStored(STORAGE_KEY_CAMPAIGN_CODE)
  );
  const [storedParticipantId, setStoredParticipantId] = useState<string | null>(() =>
    readStored(STORAGE_KEY_PARTICIPANT_ID)
  );
  const [storedAddress, setStoredAddress] = useState<string | null>(() =>
    readSession(STORAGE_KEY_ADDRESS)
  );

  // Capture research params the moment they appear in the URL — before the
  // /chat route can strip the query string. Combined into one effect so the
  // replace-on-new-link rule can act on the whole set atomically.
  useEffect(() => {
    // Nothing to capture when the URL carries no research params.
    if (!urlCampaignCode && !urlParticipantId && !urlAddress) return;

    // A different participantId means a new participant: replace the entire
    // stored set with this link's values (clearing any key this link omits)
    // so the new participant can't inherit a prior one's leftovers.
    const isNewParticipant =
      !!urlParticipantId && urlParticipantId !== readStored(STORAGE_KEY_PARTICIPANT_ID);

    if (isNewParticipant) {
      writeStored(STORAGE_KEY_CAMPAIGN_CODE, urlCampaignCode);
      writeStored(STORAGE_KEY_PARTICIPANT_ID, urlParticipantId);
      writeSession(STORAGE_KEY_ADDRESS, urlAddress);
      setStoredCampaignCode(urlCampaignCode);
      setStoredParticipantId(urlParticipantId);
      setStoredAddress(urlAddress);
      return;
    }

    // Same participant (or a link without a participantId): capture each value
    // this link carries, leaving previously stored values untouched.
    if (urlCampaignCode) {
      writeStored(STORAGE_KEY_CAMPAIGN_CODE, urlCampaignCode);
      setStoredCampaignCode(urlCampaignCode);
    }
    if (urlParticipantId) {
      writeStored(STORAGE_KEY_PARTICIPANT_ID, urlParticipantId);
      setStoredParticipantId(urlParticipantId);
    }
    if (urlAddress) {
      writeSession(STORAGE_KEY_ADDRESS, urlAddress);
      setStoredAddress(urlAddress);
    }
  }, [urlCampaignCode, urlParticipantId, urlAddress]);

  const value = useMemo<ResearchContextValue>(() => {
    const userCampaignCode = user?.campaign_code ?? null;
    const userParticipantId = user?.participant_id ?? null;

    // Apply the resolution priority documented above. `??` rather than `||`
    // so an explicit empty string from a future code path doesn't get
    // confused with "no value".
    const campaignCode = urlCampaignCode ?? storedCampaignCode ?? userCampaignCode ?? null;
    const participantId = urlParticipantId ?? storedParticipantId ?? userParticipantId ?? null;
    // Address has no user-record fallback: it's a per-link pre-fill hint only.
    const address = urlAddress ?? storedAddress ?? null;

    return {
      campaignCode,
      participantId,
      address,
      hasResearchContext: campaignCode !== null || participantId !== null,
    };
  }, [
    urlCampaignCode,
    urlParticipantId,
    urlAddress,
    storedCampaignCode,
    storedParticipantId,
    storedAddress,
    user?.campaign_code,
    user?.participant_id,
  ]);

  return <ResearchContext.Provider value={value}>{children}</ResearchContext.Provider>;
};

/**
 * Read the current research collaboration context.
 *
 * Must be used within a `<ResearchProvider>`. Returns `campaignCode`,
 * `participantId`, and a `hasResearchContext` flag.
 */
export const useResearch = (): ResearchContextValue => {
  const context = useContext(ResearchContext);
  if (!context) {
    throw new Error('useResearch must be used within a ResearchProvider');
  }
  return context;
};
